import { useRef, useState } from "react";
import { AlertCircle, Copy, RefreshCw, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { submitFeedback } from "@/lib/api";
import { useTeamsToken } from "@/lib/teamsTokenContext";
import { trackEvent } from "@/lib/telemetry";
import { Button } from "@/components/ui/Button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BugReportModal } from "./BugReportModal";

interface MessageActionsProps {
  text: string;
  messageId: string;
  requestId?: string | null;
  isLast?: boolean;
  /** The user's question that prompted this response, passed to the bug report modal. */
  question?: string;
  onRegenerate?: () => void;
  onCopyError?: (message: string) => void;
}

type Vote = "up" | "down" | null;

const DOWN_REASON_CHIPS = [
  "Wrong information",
  "Didn't understand my question",
  "Incomplete answer",
  "Other",
] as const;

type DownReason = (typeof DOWN_REASON_CHIPS)[number];

export function MessageActions({
  text,
  messageId,
  requestId = null,
  isLast = false,
  question,
  onRegenerate,
  onCopyError,
}: MessageActionsProps) {
  const teamsToken = useTeamsToken();
  const [copied, setCopied] = useState(false);
  const [vote, setVote] = useState<Vote>(null);
  const [bugReportOpen, setBugReportOpen] = useState(false);

  // Thumbs-down reason prompt state
  const [showReasonPrompt, setShowReasonPrompt] = useState(false);
  const [selectedReason, setSelectedReason] = useState<DownReason | null>(null);
  const [otherText, setOtherText] = useState("");
  const otherRef = useRef<HTMLTextAreaElement>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (err) {
      onCopyError?.(err instanceof Error ? err.message : "Could not copy to clipboard");
    }
  };

  const castUp = () => {
    const next: Vote = vote === "up" ? null : "up";
    setVote(next);
    setShowReasonPrompt(false);
    if (next) {
      void submitFeedback({ vote: "up", messageId, requestId, question: question ?? null }, teamsToken);
      trackEvent("feedback_submitted", { vote: "up", requestId: requestId ?? undefined });
    }
  };

  const castDown = () => {
    if (vote === "down") {
      // toggle off
      setVote(null);
      setShowReasonPrompt(false);
      setSelectedReason(null);
      setOtherText("");
      return;
    }
    setVote("down");
    setShowReasonPrompt(true);
  };

  const submitDownWithReason = (reason: string) => {
    void submitFeedback(
      { vote: "down", messageId, requestId, question: question ?? null, reason: reason || null },
      teamsToken,
    );
    trackEvent("feedback_submitted", { vote: "down", requestId: requestId ?? undefined });
    setShowReasonPrompt(false);
  };

  const handleReasonChip = (chip: DownReason) => {
    setSelectedReason(chip);
    if (chip !== "Other") {
      submitDownWithReason(chip);
    } else {
      // focus textarea
      setTimeout(() => otherRef.current?.focus(), 50);
    }
  };

  const handleOtherSubmit = () => {
    submitDownWithReason(otherText.trim() || "Other");
    setSelectedReason(null);
    setOtherText("");
  };

  const dismissReasonPrompt = () => {
    // vote is already set to "down"; just submit with no reason
    void submitFeedback(
      { vote: "down", messageId, requestId, question: question ?? null, reason: null },
      teamsToken,
    );
    trackEvent("feedback_submitted", { vote: "down", requestId: requestId ?? undefined });
    setShowReasonPrompt(false);
    setSelectedReason(null);
    setOtherText("");
  };

  return (
    <>
      <TooltipProvider>
        <div className="mt-2 flex gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={handleCopy}
                aria-label="Copy response"
                className={cn(
                  "sm:w-auto sm:px-2.5 sm:gap-1.5",
                  copied &&
                    "border-[var(--success)]/25 bg-[var(--success-soft)] text-[var(--success)] hover:text-[var(--success)]",
                )}
              >
                <Copy className="h-4 w-4 shrink-0" />
                <span className="hidden text-[12px] font-medium sm:inline">
                  {copied ? "Copied" : "Copy"}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copy</TooltipContent>
          </Tooltip>

          {isLast && onRegenerate && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="iconSm"
                  onClick={onRegenerate}
                  aria-label="Regenerate response"
                  className="sm:w-auto sm:px-2.5 sm:gap-1.5"
                >
                  <RefreshCw className="h-4 w-4 shrink-0" />
                  <span className="hidden text-[12px] font-medium sm:inline">Regenerate</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Regenerate response</TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={castUp}
                aria-label="Good response"
                aria-pressed={vote === "up"}
                className={cn(
                  "sm:w-auto sm:px-2.5 sm:gap-1.5",
                  vote === "up" &&
                    "border-[var(--button)]/25 bg-[var(--accent-soft)] text-[var(--button)] hover:text-[var(--button)]",
                )}
              >
                <ThumbsUp className="h-4 w-4 shrink-0" />
                <span className="hidden text-[12px] font-medium sm:inline">Good</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Good response</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={castDown}
                aria-label="Bad response"
                aria-pressed={vote === "down"}
                className={cn(
                  "sm:w-auto sm:px-2.5 sm:gap-1.5",
                  vote === "down" &&
                    "border-border-strong bg-surface-muted text-text hover:text-text",
                )}
              >
                <ThumbsDown className="h-4 w-4 shrink-0" />
                <span className="hidden text-[12px] font-medium sm:inline">Not helpful</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Bad response</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={() => setBugReportOpen(true)}
                aria-label="Report a problem with this response"
                className="sm:w-auto sm:px-2.5 sm:gap-1.5"
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span className="hidden text-[12px] font-medium sm:inline">Report</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Report a problem</TooltipContent>
          </Tooltip>
        </div>

        {/* Inline thumbs-down reason prompt */}
        {showReasonPrompt && (
          <div className="mt-3 rounded-lg border border-border bg-surface p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[12px] font-medium text-text">What was wrong with this answer?</p>
              <button
                type="button"
                onClick={dismissReasonPrompt}
                aria-label="Dismiss"
                className="rounded p-0.5 text-text-muted hover:text-text transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {DOWN_REASON_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => handleReasonChip(chip)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-[12px] font-medium transition-colors",
                    selectedReason === chip
                      ? "border-border-strong bg-surface-muted text-text"
                      : "border-border bg-surface text-text-muted hover:border-border-strong hover:text-text",
                  )}
                >
                  {chip}
                </button>
              ))}
            </div>

            {selectedReason === "Other" && (
              <div className="mt-2 flex gap-2">
                <textarea
                  ref={otherRef}
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Tell us more (optional)"
                  maxLength={200}
                  rows={2}
                  className="flex-1 resize-none rounded-md border border-border bg-surface px-3 py-2 text-[12px] text-text placeholder:text-text-muted focus:border-border-strong focus:outline-none"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleOtherSubmit}
                  className="self-end text-[12px]"
                >
                  Submit
                </Button>
              </div>
            )}
          </div>
        )}
      </TooltipProvider>

      <BugReportModal
        isOpen={bugReportOpen}
        onClose={() => setBugReportOpen(false)}
        messageContext={text.slice(0, 300)}
        question={question?.slice(0, 120)}
      />
    </>
  );
}
